// 星级（1–3 星，按步数；转一下也算一步）。标准步数从本局真实的开局棋盘推出：
//   swaps     = 块数 − 置换的环数（只用单块交换把它排好的最少步数）
//   rotations = Σ (4 − rot) mod 4（把所有旋转块点正的最少次数）
//   par       = swaps + rotations
// 门槛（只对"交换"部分放宽，旋转是固定成本、谁都得点这么多下）：
//   3 星：moves ≤ par + ⌈THREE_SLACK · swaps⌉
//   2 星：moves ≤ par + ⌈TWO_SLACK · swaps⌉
//   其余 1 星
// 背面块不改变最少步数，但会逼人盲换几次：每个背面块再放宽 FLIP_SLACK 步（只在有背面块的局）。
// 系数由模拟定（4..9 格、有无玩法各 12 局：照提示走的解法、"知道每块去哪但偶尔换错"的玩家、
// 一半步数乱换的玩家），数据见本文件末尾注释。
import { cellIndex, tileCount } from './board';
import type { Board } from './types';
import { downCount, rotationsNeeded } from './mods';

export type Par = { swaps: number; rotations: number; down?: number };

export const THREE_SLACK = 0.25;
export const TWO_SLACK = 0.75;
export const FLIP_SLACK = 0.25;

export function parOf(board: Board): Par {
  const n = board.n;
  const N = tileCount(board);
  const seen = new Array<boolean>(N).fill(false);
  let cycles = 0;
  for (let t = 0; t < N; t++) {
    if (seen[t]) continue;
    cycles += 1;
    for (let x = t; !seen[x]; x = cellIndex(board.pos[x], n)) seen[x] = true;
  }
  const down = downCount(board);
  return { swaps: N - cycles, rotations: rotationsNeeded(board), ...(down ? { down } : {}) };
}

/** 3 星 / 2 星的步数上限 */
export function starLimits(par: Par): { three: number; two: number } {
  const base = par.swaps + par.rotations + Math.ceil(FLIP_SLACK * (par.down ?? 0));
  return { three: base + Math.ceil(THREE_SLACK * par.swaps), two: base + Math.ceil(TWO_SLACK * par.swaps) };
}

export function starsFor(moves: number, par: Par): 1 | 2 | 3 {
  const lim = starLimits(par);
  return moves <= lim.three ? 3 : moves <= lim.two ? 2 : 1;
}

// 模拟数据（2026-11-04，每格数 × 玩法 12 局，8×8/9×9 各 4 局；数值 = (moves − rotations) / swaps，括号为最小–最大）：
//   照提示走：               0.73–0.99 平均（个别局 1.38，4×4 旋转）       → 基本 3 星，最差 2 星
//   知道每块去哪、10% 换错：  1.03–1.14 平均（4×4 偶有 1.55）             → 3 星
//   25% 换错：               1.19–1.38 平均                               → 3 星 / 2 星分界
//   50% 乱换：               1.56–3.98 平均（4×4 最好 1.00）               → 大多 1 星，小盘偶尔 2 星
// 于是 THREE_SLACK = 0.25（≈ 1.25×）、TWO_SLACK = 0.75（≈ 1.75×）。背面块那几局，"认真"玩家比无玩法慢约 0.02–0.05，
// 每个背面块再放 0.25 步（≈ +0.08×）补偿盲换。脚本是一次性的（不进仓库，不是单元测试）。
